import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { KPICarousel, KPICard } from "../../../Components/KPICarousel";
import { getFleetPerformanceKpis } from "../../../Api/FleetPerformanceApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { formatCompactEnergy } from "../Components/FleetCommonFunctions";

const EnergyConsumptionKPISection = ({ startDate, endDate, globalFilters = {}, vehiclesConnected, showHeader = true }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [kpiData, setKpiData] = useState([]);

    // Helper function to calculate percentage change
    // Set lowerIsBetter=true for metrics where decrease is positive (e.g., energy consumption)
    const calculatePercentageChange = React.useCallback((current, previous, lowerIsBetter = false) => {
        if (!previous || previous === 0) return { value: 0, trend: "neutral", isPositive: true };
        const change = ((current - previous) / previous) * 100;

        let trend;
        let isPositive;

        if (change > 0) {
            trend = "up";
            isPositive = !lowerIsBetter;
        } else if (change < 0) {
            trend = "down";
            isPositive = lowerIsBetter;
        } else {
            trend = "neutral";
            isPositive = true;
        }

        return {
            value: Math.abs(change).toFixed(1),
            trend: trend,
            isPositive: isPositive
        };
    }, []);

    // Helper function to check if comparison should be shown
    const shouldShowComparison = React.useCallback((start, end) => {
        if (!start || !end) return true;

        const startDateObj = new Date(start);
        startDateObj.setHours(0, 0, 0, 0); // Start of day

        const endDateObj = new Date(end);
        endDateObj.setHours(23, 59, 59, 999); // End of day

        // Calculate duration in days (rounded)
        const durationDays = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));

        // Calculate comparison start date: subtracting duration days from start date
        // Create new date object to avoid mutating startDateObj
        const comparisonStartDate = new Date(startDateObj);
        comparisonStartDate.setDate(comparisonStartDate.getDate() - durationDays);

        // Calculate limit: today - 3 months
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today to start of day

        const limitDate = new Date(today);
        limitDate.setMonth(today.getMonth() - 3);

        // Check if comparison start date is >= limit date
        return comparisonStartDate.getTime() >= limitDate.getTime();
    }, []);

    // Helper function to calculate trend period text
    const getTrendPeriodText = React.useCallback((start, end) => {
        if (!start || !end) return "vs. Last 30 Days";

        if (!shouldShowComparison(start, end)) {
            return null;
        }

        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        const today = new Date();
        const todayEnd = new Date();

        startDateObj.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        endDateObj.setHours(23, 59, 59, 999);
        todayEnd.setHours(23, 59, 59, 999);

        if (startDateObj.getTime() === today.getTime() &&
            endDateObj.getTime() <= todayEnd.getTime()) {
            return "vs. Yesterday";
        }

        const daysDiff = Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
        return `vs. Last ${daysDiff} Day${daysDiff === 1 ? '' : 's'}`;
    }, [shouldShowComparison]);

    // Fetch KPI data
    useEffect(() => {
        const fetchKPIData = async () => {
            if (!startDate || !endDate) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const filters = { startDate, endDate };
                const res = await getFleetPerformanceKpis(globalFilters, filters);

                let apiData = {};
                if (res?.responseStatus?.code === CODE.SUCCESS) {
                    apiData = res?.response?.data || {};
                } else {
                    throw new Error("Failed to fetch KPI data");
                }

                const trendPeriod = getTrendPeriodText(startDate, endDate);
                const showComparison = trendPeriod !== null;

                // 1. Avg Energy Consumption per Km
                const avgConsumptionChange = calculatePercentageChange(
                    apiData.avgEnergyConsumptionPerKm,
                    apiData.avgEnergyConsumptionPerKmInPreviousTimerange,
                    true // Lower is better
                );

                // 2. Total Charging Energy
                const chargingEnergyChange = calculatePercentageChange(
                    apiData.totalChargingEnergy,
                    apiData.totalChargingEnergyInPreviousTimerange,
                    true
                );

                // Use ratios directly from API response
                const fastPct = Math.round(apiData.fastChargingRatio || 0);
                const slowPct = Math.round(apiData.slowChargingRatio || 0);

                // 3. Total Discharging Energy
                const dischargingEnergyChange = calculatePercentageChange(
                    apiData.totalDischargeEnergy,
                    apiData.totalDischargeEnergyInPreviousTimerange,
                    false
                );

                // 4. Regeneration Energy
                const regenEnergyChange = calculatePercentageChange(
                    apiData.totalRegenEnergy,
                    apiData.totalRegenEnergyInPreviousTimerange,
                    false // Higher is better
                );

                const mappedData = [
                    {
                        title: "Avg kwh/km",
                        titleTooltip: "Average energy consumed per kilometer across all vehicles in the fleet during the selected period.",
                        value: `${Number(apiData.avgEnergyConsumptionPerKm || 0).toFixed(2)}`,
                        unit: "kWh/km",
                        previousValue: showComparison ? `${Number(apiData.avgEnergyConsumptionPerKmInPreviousTimerange || 0).toFixed(2)}` : null,
                        trend: showComparison ? avgConsumptionChange.trend : null,
                        isPositive: showComparison ? avgConsumptionChange.isPositive : true,
                        trendValue: showComparison ? `${avgConsumptionChange.value}%` : null,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: "Best Vehicle",
                                value: `${Number(apiData.minEnergyConsumptionPerKm || 0).toFixed(2)} kWh/km`
                            },
                            {
                                label: "Worst Vehicle",
                                value: `${Number(apiData.maxEnergyConsumptionPerKm || 0).toFixed(2)} kWh/km`
                            },
                        ],
                    },
                    (() => {
                        const f = formatCompactEnergy(apiData.totalChargingEnergy);
                        const sessionF = formatCompactEnergy(apiData.averageEnergyPerChargingSession);
                        return {
                            title: "Total Charging Energy",
                            titleTooltip: "Total energy consumed during charging sessions across all vehicles in the fleet.",
                            value: f.displayValue,
                            unit: f.unit,
                            previousValue: showComparison ? Math.round((apiData.totalChargingEnergyInPreviousTimerange || 0) / f.divisor).toString() : null,
                            trend: showComparison ? chargingEnergyChange.trend : null,
                            isPositive: showComparison ? chargingEnergyChange.isPositive : true,
                            trendValue: showComparison ? `${chargingEnergyChange.value}%` : null,
                            trendPeriod: trendPeriod,
                            metrics: [
                                { label: "Fast vs Slow Charging Ratio", value: `${fastPct}:${slowPct}` },
                                { label: "Avg. Energy / Charge Session", value: `${sessionF.displayValue} ${sessionF.unit}` },
                            ],
                        };
                    })(),
                    (() => {
                        const f = formatCompactEnergy(apiData.totalDischargeEnergy);
                        const perTripF = formatCompactEnergy(apiData.averageDischargePerDischargingSession);
                        return {
                            title: "Total Discharging Energy",
                            titleTooltip: "Total energy discharged from batteries across all vehicles during trips and operations.",
                            value: f.displayValue,
                            unit: f.unit,
                            previousValue: showComparison ? Math.round((apiData.totalDischargeEnergyInPreviousTimerange || 0) / f.divisor).toString() : null,
                            trend: showComparison ? dischargingEnergyChange.trend : null,
                            isPositive: showComparison ? dischargingEnergyChange.isPositive : true,
                            trendValue: showComparison ? `${dischargingEnergyChange.value}%` : null,
                            trendPeriod: trendPeriod,
                            metrics: [
                                { label: "Avg Discharge Energy per trip", value: `${perTripF.displayValue} ${perTripF.unit}` },
                                { label: "High Discharge Events", value: (apiData.numberOfHighDischargeSessions || 0).toString() },
                            ],
                        };
                    })(),
                    (() => {
                        const f = formatCompactEnergy(apiData.totalRegenEnergy);
                        const regenTripF = formatCompactEnergy(apiData.avergeRegenPerDischargingSession);
                        return {
                            title: "Regeneration Energy",
                            titleTooltip: "Total energy recovered through regenerative braking across all vehicles in the fleet.",
                            value: f.displayValue,
                            unit: f.unit,
                            previousValue: showComparison ? Math.round((apiData.totalRegenEnergyInPreviousTimerange || 0) / f.divisor).toString() : null,
                            trend: showComparison ? regenEnergyChange.trend : null,
                            isPositive: showComparison ? regenEnergyChange.isPositive : true,
                            trendValue: showComparison ? `${regenEnergyChange.value}%` : null,
                            trendPeriod: trendPeriod,
                            metrics: [
                                { label: "Regen Contribution (% of Discharge)", value: `${Number(apiData.regenContribution || 0).toFixed(1)}%` },
                                { label: "Regen per Trip", value: `${regenTripF.displayValue} ${regenTripF.unit}` },
                            ],
                        };
                    })(),
                ];

                setKpiData(mappedData);
            } catch (err) {
                console.error("Error fetching KPI data:", err);
                setError(err?.message || "Failed to load KPI data");
            } finally {
                setLoading(false);
            }
        };

        fetchKPIData();
    }, [startDate, endDate, globalFilters]);

    const renderKPICard = (kpi) => (
        <KPICard
            title={kpi.title}
            titleTooltip={kpi.titleTooltip}
            value={kpi.value}
            unit={kpi.unit}
            previousValue={kpi.previousValue}
            trend={kpi.trend}
            isPositive={kpi.isPositive}
            trendValue={kpi.trendValue}
            trendPeriod={kpi.trendPeriod}
            metrics={kpi.metrics}
            totalVehicles={vehiclesConnected}
        />
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* KPI Section Header */}
            {showHeader && (
                <Box>
                    <Typography
                        variant="fw400s20c000"
                        sx={{
                            lineHeight: "28px",
                            fontWeight: 300,
                        }}
                    >
                        Key Performance Indicators
                    </Typography>
                </Box>
            )}

            {/* KPI Carousel */}
            <KPICarousel
                data={kpiData}
                renderCard={renderKPICard}
                loading={loading}
                error={error}
            />
        </Box>
    );
};

export default EnergyConsumptionKPISection;
